import { useApi, useToaster } from "@shopexperts/shared";
import { useInfiniteQuery } from "@tanstack/vue-query";
import { computed, watch } from "vue";

interface UseExpertLeadsOptions {
  /** Number of items per page. Defaults to 15. */
  limit?: number;
}

/**
 * Composable for fetching paginated leads matched to the authenticated expert.
 * 
 * Uses the expert leads V2 API with cursor-based pagination.
 * 
 * @param options - Optional configuration
 *   - limit: Number of leads per page (default: 15)
 * 
 * @example
 * ```ts
 * const { leads, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useExpertLeads();
 * 
 * // Load more leads
 * if (hasNextPage.value) {
 *   await fetchNextPage();
 * }
 * ```
 */
export function useExpertLeads(options: UseExpertLeadsOptions = {}) {
  const api = useApi();
  const toaster = useToaster();

  const { limit = 15 } = options;

  const {
    data,
    isLoading,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
    isRefetching,
  } = useInfiniteQuery({
    queryKey: ['expert', 'leads-v2', 'list'],
    queryFn: async ({ pageParam }) => {
      return await api.expert.leadsV2.listLeads({
        query: {
          limit,
          cursor: pageParam,
        },
      });
    },
    getNextPageParam: (lastPage) => {
      return lastPage.pagination.has_next ? lastPage.pagination.cursor : undefined;
    },
    initialPageParam: undefined as string | undefined,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const leads = computed(() => {
    return data.value?.pages.flatMap((page) => page.data) || [];
  });

  const totalCount = computed(() => {
    return leads.value.length;
  });

  watch(error, (err) => {
    if (err) {
      toaster.create({
        title: "Failed to load leads",
        type: "error",
      });
      console.error("[useExpertLeads] Error:", err);
    }
  });

  return {
    leads,
    totalCount,
    isLoading,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
    isRefetching,
  };
}
